/**
 * Harness FME (Split) browser SDK client.
 *
 * Lazy singleton around the Split browser SDK. Supports two modes through the
 * same `initSplitClient` entry point:
 * - **localhost mode** (`authorizationKey: "localhost"`) for local dev and
 *   tests — treatments are served from an in-memory `features` map, so no
 *   network, SDK key or Harness account is required. `LOCAL_FEATURES` is that
 *   map: add a flag here to mock it locally before it exists in Harness.
 * - **network mode** (a real client-side SDK key) for staging/production.
 *
 * We use `@splitsoftware/splitio-browserjs` (not the isomorphic
 * `@splitsoftware/splitio`): the browser package uses `LocalhostFromObject`
 * everywhere, so the inline `features` map works in Vite AND jsdom. The
 * isomorphic package falls back to `LocalhostFromFile` under Node/jsdom and
 * ignores the inline map.
 */
import { SplitFactory } from "@splitsoftware/splitio-browserjs";
import type { FlagName } from "@/config/flagsConfig";

export type SplitClientConfig = {
  /** `"localhost"` for local/mock mode, otherwise a client-side SDK key. */
  authorizationKey: string;
  /** Traffic key identifying the current entity (account/user uuid, ...). */
  key: string;
  /** In-memory treatments for localhost mode; ignored in network mode. */
  features?: SplitIO.MockedFeaturesMap;
};

/**
 * Local dev/test mock. Flags not yet configured in Harness live here so they
 * can be exercised locally. Add `FLAG_NAME: "on" | "off"` entries as needed.
 */
export const LOCAL_FEATURES: SplitIO.MockedFeaturesMap = {
  EXEMPTIONS_MULTI_CONDITION: "on",
};

let factory: SplitIO.IBrowserSDK | null = null;
let client: SplitIO.IBrowserClient | null = null;

/** Maps a Split treatment to a boolean flag value. Only `"on"` is truthy. */
export const treatmentToBoolean = (treatment: SplitIO.Treatment): boolean =>
  treatment === "on";

/** Builds a localhost-mode config from the {@link LOCAL_FEATURES} mock map. */
export const createLocalhostConfig = (key: string): SplitClientConfig => ({
  authorizationKey: "localhost",
  key,
  features: LOCAL_FEATURES,
});

/**
 * Initialises the Split client singleton (idempotent — subsequent calls return
 * the existing client and ignore the passed config).
 */
export const initSplitClient = (
  config: SplitClientConfig,
): SplitIO.IBrowserClient => {
  if (client) return client;

  factory = SplitFactory({
    core: {
      authorizationKey: config.authorizationKey,
      key: config.key,
    },
    ...(config.features ? { features: config.features } : {}),
    debug: false,
  });
  client = factory.client();
  return client;
};

/** Returns the current Split client, or `null` if not initialised. */
export const getSplitClient = (): SplitIO.IBrowserClient | null => client;

/**
 * Resolves once the SDK has finished its initial load. Rejects if the client
 * has not been initialised.
 */
export const whenSplitReady = (): Promise<void> => {
  if (!client) {
    return Promise.reject(new Error("Split client is not initialised."));
  }
  return client.ready();
};

/**
 * Returns the raw treatment for a flag. Yields `"control"` when the client is
 * not initialised or not ready yet.
 */
export const getTreatment = (flagName: FlagName): SplitIO.Treatment =>
  client ? client.getTreatment(flagName) : "control";

/** Returns whether a flag's treatment is `"on"`. */
export const isFlagEnabled = (flagName: FlagName): boolean =>
  treatmentToBoolean(getTreatment(flagName));

/** Destroys the client and resets the singleton (flushes pending work). */
export const destroySplitClient = async (): Promise<void> => {
  if (client) {
    await client.destroy();
  }
  client = null;
  factory = null;
};
